import type { BrowserOptions } from "@sentry/browser";
import { getAuthRootUrl } from "shared/config/AppConfigUtils";
import { isDev } from "shared/utils/DevUtils";

// From https://vitejs.dev/guide/env-and-mode.html#env-files
//  During production, these env variables are statically replaced.
//  It is therefore necessary to always reference them using the full static string.
//
// prettier-ignore is used to avoid line breaks.
//
// Another important note from the Vite documentation:
//  To prevent accidentally leaking env variables to the client,
//  only variables prefixed with VITE_ are exposed to your Vite-processed code.

// prettier-ignore
const VITE_SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
// prettier-ignore
const VITE_SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT;
// prettier-ignore
const VITE_SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE;
// prettier-ignore
const VITE_STYTCH_PROJECT_ID = import.meta.env.VITE_STYTCH_PROJECT_ID;
// prettier-ignore
const VITE_INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;
// prettier-ignore
const VITE_ENABLE_SUBDOMAINS = import.meta.env.VITE_ENABLE_SUBDOMAINS;
// prettier-ignore
const VITE_LOGROCKET_APP_ID = import.meta.env.VITE_LOGROCKET_APP_ID;

const { hostname } = window.location;

const authRootUrl = getAuthRootUrl(
  new URL(window.location.toString()),
  VITE_ENABLE_SUBDOMAINS,
);

const isLocalhost = hostname === "localhost";

const isSubdomain = authRootUrl.hostname !== hostname && !isLocalhost;

const isAuthOnly = !isSubdomain && !isLocalhost;

const disableSentry = isDev || !VITE_SENTRY_DSN;

const disableStytch =
  !VITE_STYTCH_PROJECT_ID ||
  (VITE_ENABLE_SUBDOMAINS && isSubdomain) ||
  ("global" in window && global.disableStytch);

export const AppConfig = {
  ...(VITE_ENABLE_SUBDOMAINS
    ? {
        subdomains: {
          authRootUrl,

          // If subdomains are enabled and the app is on a customer subdomain then
          // auth routes are not available and will redirect to the root domain.
          isSubdomain,

          // If subdomains are enabled but the app is not on a customer subdomain then
          // only auth/account routes are available.
          isAuthOnly,
        },
      }
    : {}),
  sentry: disableSentry
    ? undefined
    : ({
        // From .env.production
        dsn: VITE_SENTRY_DSN,

        // From github actions workflow
        environment: VITE_SENTRY_ENVIRONMENT,

        // From build-release.sh
        release: VITE_SENTRY_RELEASE,
      } as Partial<BrowserOptions>),
  stytch: disableStytch
    ? undefined
    : {
        projectId: VITE_STYTCH_PROJECT_ID,
      },
  intercom: {
    appId: VITE_INTERCOM_APP_ID,
  },
  logRocket:
    !isDev && VITE_LOGROCKET_APP_ID
      ? {
          appId: VITE_LOGROCKET_APP_ID,
        }
      : undefined,
};
